import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import client from '../../sanity/client';
import '../Transmission/SubPageTransmission.scss';
import { useLanguage } from '../../components/LanguageContext';
import useScrollPosition from '../../hooks/useScrollPosition';

const getText = (textObj, language) => {
  if (!textObj) return '';
  if (typeof textObj === 'string') return textObj;
  return textObj[language] || textObj.fr || '';
};

const ParutionsDetail = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [item, setItem] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 992);
  
  // Hook pour gérer la position de scroll
  useScrollPosition();

  const handleResize = () => {
    setIsMobileView(window.innerWidth <= 992);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobileView) return; // Pas de gestion sticky sur mobile

    const handleScroll = () => {
      const container = document.querySelector('.exposition-item');
      const textContent = container?.querySelector('.text-content');
      const textScrollableContent = container?.querySelector('.text-scrollable-content');
      const textInner = container?.querySelector('.text-inner');
      
      if (!textContent || !textInner || !textScrollableContent) return;

      const containerRect = container.getBoundingClientRect();
      
      // Calculer les hauteurs
      const textHeight = textInner.scrollHeight;
      const containerHeight = textScrollableContent.clientHeight;
      const maxScrollDistance = textHeight - containerHeight;
      
      // Si le texte tient dans le conteneur, pas besoin de scroll
      if (maxScrollDistance <= 0) {
        textInner.style.transform = 'translateY(0px)';
        return;
      }
      
      // Calculer le progrès du scroll
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;
      const viewportHeight = window.innerHeight;
      
      if (containerBottom < 0 || containerTop > viewportHeight) {
        return;
      }
      
      const sectionHeight = container.offsetHeight;
      const startScrollThreshold = 0.3; // Réduit de 0.6 à 0.3 - démarre plus tôt
      const endScrollThreshold = 0.98;   // Augmenté de 0.95 à 0.98 - finit plus tard
      
      const rawProgress = (viewportHeight - containerTop) / (sectionHeight + viewportHeight);
      
      // Le texte reste fixe tant qu'on n'a pas atteint le seuil de démarrage
      if (rawProgress < startScrollThreshold) {
        textInner.style.transform = 'translateY(0px)';
        return;
      }
      
      // Calculer le progrès ajusté seulement après le seuil
      const adjustedProgress = Math.max(0, Math.min(1, 
        (rawProgress - startScrollThreshold) / (endScrollThreshold - startScrollThreshold)
      ));
      
      const scrollOffset = adjustedProgress * maxScrollDistance;
      textInner.style.transform = `translateY(-${scrollOffset}px)`;
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileView, item]);

  useEffect(() => {
    const fetchData = async () => {
        const query = `*[_type == "poeteSectionParutions"][0].parutions[slug.current == $slug][0]{ 
            title,
            description,
            link{
              url,
              text
            },
            gallery[]{ "imageUrl": asset->url + "?auto=format" }
          }`;
      const result = await client.fetch(query, { slug });
      setItem(result);
    };

    fetchData();
  }, [slug]);

  if (!item) return <div>Loading...</div>;

  return (
    <div className="subpage-transmission">
      <div className="exposition-item">
        {isMobileView ? (
          <>
            <div className="text-content-h1 mobile-title parution-title">
              <h1>{item.title?.[language] || item.title || 'Sans titre'}</h1>
              {item.link && item.link.url && (
                <div className="external-link" style={{ marginTop: '15px' }}>
                  <a 
                    href={item.link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#333',
                      textDecoration: 'underline'
                    }}
                  >
                    {item.link.text || 'Voir en ligne'}
                  </a>
                </div>
              )}
            </div>
            <div className="image-content">
              {item.gallery && item.gallery.map((img, idx) => (
                <img
                  key={idx}
                  src={img.imageUrl}
                  alt={`Image ${idx}`}
                  className="gallery-image"
                />
              ))}
            </div>
            <div className="text-content">
              <div className="text-content-1">
                {getText(item.description, language).split('\n').map((line, idx) => (
                  <span key={idx}>
                    {line}<br />
                  </span>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="image-content">
              {item.gallery && item.gallery.map((img, idx) => (
                <img
                  key={idx}
                  src={img.imageUrl}
                  alt={`Image ${idx}`}
                  className="gallery-image"
                />
              ))}
            </div>
                        <div className="text-content sticky">
              <div className="text-content-h1 parution-title">
                <h1>{item.title?.[language] || item.title || 'Sans titre'}</h1>
                {item.link && item.link.url && (
                  <div style={{ marginTop: '15px' }}>
                    <a 
                      href={item.link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#333',
                        textDecoration: 'underline',
                        fontSize: '16px'
                      }}
                    >
                      {item.link.text || 'Voir en ligne'}
                    </a>
                  </div>
                )}
              </div>
              <div className="text-scrollable-content">
                <div className="text-inner">
                  <div className="text-content-1">
                    {getText(item.description, language).split('\n').map((line, idx) => (
                      <span key={idx}>
                        {line}<br />
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ParutionsDetail;